import React, { Fragment } from "react";
import { Button, Col, FormGroup, Row } from "reactstrap";
import { connect } from "react-redux";
import { firebaseConnect } from "react-redux-firebase";
import get from "lodash.get";
import withStyles from "isomorphic-style-loader/withStyles";
import BaseSelect from "../../../toolkit/baseSelect";
import ModalFrame from "../../../toolkit/modalFrame";
import SnackbarNotification from "../../../toolkit/snackbarNotification";
import { AlertColor } from "@mui/material";
import { serviceCaller, ServiceCallerProps } from "../../composition";
import { isCurrentUserAdmin } from "../../../utils/roleCheck";
import {
  bulkUpsertThankYouAdminLists,
  removeThankYouAdminGlobal,
  removeThankYouAdminList,
  upsertThankYouAdminGlobal,
  upsertThankYouAdminList,
} from "../../../utils/fetch";
import IThankYouAdminEmail from "../../../types/services/IThankYouAdminEmail";
import ISegment from "../../../types/services/ISegment";
import s from "./styles.scss";
import {
  assignmentRowsFromMap,
  assignedStaffKeys,
  assignableStaffOptions,
  formatDollarAmount,
  isOrdinaryDonorList,
  isStaffAlreadyAssigned,
  listIdsForUser,
  StaffUserOption,
  staffAvailableToAssign,
  staffDisplayName,
} from "./helpers";
import ThresholdFields, {
  BLANK_THRESHOLDS,
  readThresholds,
  ThresholdState,
} from "./thresholdFields";

interface SharedDataProps {
  thankYouAdminEmails: { [userId: string]: IThankYouAdminEmail };
  thankYouAdminSegments: {
    [segmentId: string]: { [userId: string]: IThankYouAdminEmail };
  };
  users: { [userId: string]: any };
  segments: { [segmentId: string]: ISegment };
}

interface BlockState {
  addUserId: string;
  addFields: ThresholdState;
  editUserId: string | null;
  editFields: ThresholdState;
  error: string;
  busy: boolean;
  snackbarOpen: boolean;
  snackbarMessage: string;
  snackbarSeverity: AlertColor;
  addScopeIds: string[];
  editScopeId: string | null;
}

const initialBlockState: BlockState = {
  addUserId: "",
  addFields: { ...BLANK_THRESHOLDS },
  editUserId: null,
  editFields: { ...BLANK_THRESHOLDS },
  error: "",
  busy: false,
  snackbarOpen: false,
  snackbarMessage: "",
  snackbarSeverity: "success",
  addScopeIds: [],
  editScopeId: null,
};

function CellNumberInput(props: {
  id: string;
  name: string;
  value: string;
  disabled?: boolean;
  onChange: (value: string) => void;
}) {
  return (
    <input
      className={s.cellInput}
      type="number"
      id={props.id}
      name={props.name}
      value={props.value}
      disabled={props.disabled}
      onChange={event => props.onChange(event.target.value)}
    />
  );
}

class ThankYouAdminsPanel extends React.Component<
  ServiceCallerProps &
    SharedDataProps & {
      segmentId?: string;
      userId?: string;
      selectedSegmentIds?: string[];
      hideTitle?: boolean;
      hideAdd?: boolean;
      addOnly?: boolean;
      isOpen?: boolean;
      onClose?: () => void;
      onAddSuccess?: () => void;
    },
  BlockState
> {
  state: BlockState = { ...initialBlockState };

  canEdit = () => isCurrentUserAdmin(this.props.currentCharity);

  adminUrl = () => this.props.config && this.props.config.adminServiceUrl;

  charityId = () => this.props.currentCharity && this.props.currentCharity.id;

  token = () => this.props.serviceAccessToken;

  showError = (error: any) => {
    const message =
      error && error.message
        ? error.message
        : String(error || "Request failed");
    this.setState({ error: message, busy: false });
  };

  showToast = (message: string, severity: AlertColor = "success") => {
    this.setState({
      snackbarOpen: true,
      snackbarMessage: message,
      snackbarSeverity: severity,
      busy: false,
      error: "",
    });
  };

  runWrite = (
    work: () => Promise<void>,
    successMessage: string,
    afterSuccess?: () => void
  ) => {
    this.setState({ busy: true, error: "" }, () => {
      work()
        .then(() => {
          this.showToast(successMessage);
          this.setState({
            addUserId: "",
            addFields: { ...BLANK_THRESHOLDS },
            addScopeIds: [],
            editUserId: null,
            editScopeId: null,
          });
          if (afterSuccess) {
            afterSuccess();
          }
        })
        .catch(this.showError);
    });
  };

  liveName = (row: IThankYouAdminEmail & { userId: string }) => {
    const user = this.props.users && this.props.users[row.userId];
    return (user && staffDisplayName(user)) || row.name || row.userId;
  };

  liveEmail = (row: IThankYouAdminEmail & { userId: string }) => {
    const user = this.props.users && this.props.users[row.userId];
    return (user && user.email) || row.email;
  };

  upsertGlobal = (
    userId: string,
    thresholds: ReturnType<typeof readThresholds>
  ) => {
    if (!thresholds) return;
    this.runWrite(
      () =>
        upsertThankYouAdminGlobal(
          this.adminUrl(),
          this.charityId(),
          userId,
          this.token(),
          thresholds.threshold,
          thresholds.firstMonthlyThreshold,
          thresholds.maxThreshold,
          thresholds.maxFirstMonthlyThreshold
        ),
      "Global thank-you assignment saved",
      this.props.onAddSuccess
    );
  };

  upsertList = (
    segmentId: string,
    userId: string,
    thresholds: ReturnType<typeof readThresholds>
  ) => {
    if (!thresholds) return;
    this.runWrite(
      () =>
        upsertThankYouAdminList(
          this.adminUrl(),
          this.charityId(),
          segmentId,
          userId,
          this.token(),
          thresholds.threshold,
          thresholds.firstMonthlyThreshold,
          thresholds.maxThreshold,
          thresholds.maxFirstMonthlyThreshold
        ),
      "Thank you segment assignment saved",
      this.props.onAddSuccess
    );
  };

  handleAdd = () => {
    const parsed = readThresholds(this.state.addFields);
    if (!parsed || !this.state.addUserId) {
      this.setState({
        error: "Choose a user and enter valid whole-dollar ranges.",
      });
      return;
    }
    if (this.props.segmentId) {
      this.upsertList(this.props.segmentId, this.state.addUserId, parsed);
      return;
    }
    this.upsertGlobal(this.state.addUserId, parsed);
  };

  handleSaveEdit = (userId: string) => {
    const parsed = readThresholds(this.state.editFields);
    if (!parsed) {
      this.setState({ error: "Enter valid whole-dollar ranges." });
      return;
    }
    if (this.props.segmentId) {
      this.upsertList(this.props.segmentId, userId, parsed);
      return;
    }
    this.upsertGlobal(userId, parsed);
  };

  handleRemove = (userId: string) => {
    if (this.props.segmentId) {
      this.runWrite(
        () =>
          removeThankYouAdminList(
            this.adminUrl(),
            this.charityId(),
            this.props.segmentId,
            userId,
            this.token()
          ),
        "Thank you segment assignment removed"
      );
      return;
    }
    this.runWrite(
      () =>
        removeThankYouAdminGlobal(
          this.adminUrl(),
          this.charityId(),
          userId,
          this.token()
        ),
      "Removed from Global"
    );
  };

  renderIconButton(
    label: string,
    icon: string,
    onClick: () => void,
    disabled?: boolean
  ) {
    return (
      <button
        type="button"
        className={s.iconButton}
        aria-label={label}
        title={label}
        disabled={disabled}
        onClick={onClick}
      >
        <i className={`fa ${icon}`} aria-hidden="true" />
      </button>
    );
  }

  renderRows(
    rows: Array<IThankYouAdminEmail & { userId: string }>,
    onRemove: (userId: string) => void
  ) {
    if (rows.length === 0) {
      return <p className={s.empty}>{this.emptyText()}</p>;
    }
    return (
      <table className={s.table}>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th className={s.amount}>Gift min ($)</th>
            <th className={s.amount}>Gift max ($)</th>
            <th className={s.amount}>First monthly min ($)</th>
            <th className={s.amount}>First monthly max ($)</th>
            {this.canEdit() && <th className={s.actions}> </th>}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => {
            const editing = this.state.editUserId === row.userId;
            return (
              <tr key={row.userId}>
                <td>{this.liveName(row)}</td>
                <td>{this.liveEmail(row)}</td>
                <td className={s.amount}>
                  {editing ? (
                    <CellNumberInput
                      name={`edit-threshold-${row.userId}`}
                      id={`edit-threshold-${row.userId}`}
                      value={this.state.editFields.threshold}
                      onChange={threshold =>
                        this.setState({
                          editFields: {
                            ...this.state.editFields,
                            threshold,
                          },
                        })
                      }
                    />
                  ) : (
                    formatDollarAmount(row.threshold)
                  )}
                </td>
                <td className={s.amount}>
                  {editing ? (
                    <CellNumberInput
                      name={`edit-max-threshold-${row.userId}`}
                      id={`edit-max-threshold-${row.userId}`}
                      value={this.state.editFields.maxThreshold}
                      onChange={maxThreshold =>
                        this.setState({
                          editFields: {
                            ...this.state.editFields,
                            maxThreshold,
                          },
                        })
                      }
                    />
                  ) : (
                    formatDollarAmount(row.maxThreshold)
                  )}
                </td>
                <td className={s.amount}>
                  {editing ? (
                    <CellNumberInput
                      name={`edit-monthly-${row.userId}`}
                      id={`edit-monthly-${row.userId}`}
                      value={this.state.editFields.firstMonthlyThreshold}
                      onChange={firstMonthlyThreshold =>
                        this.setState({
                          editFields: {
                            ...this.state.editFields,
                            firstMonthlyThreshold,
                          },
                        })
                      }
                    />
                  ) : (
                    formatDollarAmount(row.firstMonthlyThreshold)
                  )}
                </td>
                <td className={s.amount}>
                  {editing ? (
                    <CellNumberInput
                      name={`edit-max-monthly-${row.userId}`}
                      id={`edit-max-monthly-${row.userId}`}
                      value={this.state.editFields.maxFirstMonthlyThreshold}
                      onChange={maxFirstMonthlyThreshold =>
                        this.setState({
                          editFields: {
                            ...this.state.editFields,
                            maxFirstMonthlyThreshold,
                          },
                        })
                      }
                    />
                  ) : (
                    formatDollarAmount(row.maxFirstMonthlyThreshold)
                  )}
                </td>
                {this.canEdit() && (
                  <td className={s.actions}>
                    {editing ? (
                      <div className={s.rowActions}>
                        <Button
                          className={s.button}
                          disabled={this.state.busy}
                          onClick={() => this.handleSaveEdit(row.userId)}
                        >
                          Save
                        </Button>
                        <Button
                          className={s.button}
                          onClick={() => this.setState({ editUserId: null })}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <div className={s.rowActions}>
                        {this.renderIconButton("Edit", "fa-edit", () =>
                          this.setState({
                            editUserId: row.userId,
                            editFields: {
                              threshold: String(
                                row.threshold == null ? "" : row.threshold
                              ),
                              maxThreshold: String(
                                row.maxThreshold == null ? "" : row.maxThreshold
                              ),
                              firstMonthlyThreshold: String(
                                row.firstMonthlyThreshold == null
                                  ? ""
                                  : row.firstMonthlyThreshold
                              ),
                              maxFirstMonthlyThreshold: String(
                                row.maxFirstMonthlyThreshold == null
                                  ? ""
                                  : row.maxFirstMonthlyThreshold
                              ),
                            },
                          })
                        )}
                        {this.renderIconButton(
                          "Remove",
                          "fa-trash",
                          () => onRemove(row.userId),
                          this.state.busy
                        )}
                      </div>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  }

  emptyText() {
    if (this.props.segmentId) {
      return "No thank-you users assigned to this segment.";
    }
    return "No global thank-you users.";
  }

  renderAdd(userOptions: StaffUserOption[], emptyText: string) {
    if (!this.canEdit()) {
      return null;
    }
    if (userOptions.length === 0) {
      return <p className={s.empty}>{emptyText}</p>;
    }
    const fieldId = this.props.segmentId ? "segment" : "global";
    const selectOptions = userOptions.map(option => ({
      label: `${option.label} (${option.email})`,
      value: option.id,
    }));
    return (
      <div className={s.addRow}>
        <Row>
          <Col sm={6}>
            <FormGroup className={s.staffSelect}>
              <label className={s.label} htmlFor={`${fieldId}-user`}>
                User
              </label>
              <BaseSelect
                id={`${fieldId}-user`}
                name={`${fieldId}-user`}
                dataElmId={`${fieldId}-user`}
                placeholder="Select user..."
                options={selectOptions}
                value={
                  selectOptions.find(
                    option => option.value === this.state.addUserId
                  ) || null
                }
                onChange={selection =>
                  this.setState({ addUserId: selection ? selection.value : "" })
                }
              />
            </FormGroup>
          </Col>
        </Row>
        <ThresholdFields
          idPrefix={`${fieldId}-add`}
          fields={this.state.addFields}
          disabled={this.state.busy}
          onChange={addFields => this.setState({ addFields })}
        />
        <Button
          className={s.button}
          disabled={this.state.busy || !this.state.addUserId}
          onClick={this.handleAdd}
        >
          Add
        </Button>
      </div>
    );
  }
}

class GlobalThankYouAdminsBlockInner extends ThankYouAdminsPanel {
  render() {
    const rows = assignmentRowsFromMap(this.props.thankYouAdminEmails, {
      charityId: this.charityId(),
      users: this.props.users,
    });
    const userOptions = staffAvailableToAssign(
      this.props.users,
      this.props.thankYouAdminEmails,
      this.charityId()
    );
    return (
      <section className={this.props.hideTitle ? s.pageBody : s.block}>
        {!this.props.hideTitle && (
          <h2 className={s.title}>Global Thank You Users</h2>
        )}
        {!this.props.addOnly && this.renderRows(rows, this.handleRemove)}
        {!this.props.hideAdd &&
          this.renderAdd(
            userOptions,
            "All assignable users are already Global thank-you users."
          )}
        {this.state.error && <p className={s.error}>{this.state.error}</p>}
        <SnackbarNotification
          open={this.state.snackbarOpen}
          message={this.state.snackbarMessage}
          severity={this.state.snackbarSeverity}
          onClose={() => this.setState({ snackbarOpen: false })}
        />
      </section>
    );
  }
}

class SegmentThankYouAdminsBlockInner extends ThankYouAdminsPanel {
  render() {
    const rows = assignmentRowsFromMap(
      this.props.thankYouAdminSegments &&
        this.props.segmentId &&
        this.props.thankYouAdminSegments[this.props.segmentId],
      {
        charityId: this.charityId(),
        users: this.props.users,
      }
    );
    const userOptions = staffAvailableToAssign(
      this.props.users,
      this.props.thankYouAdminSegments &&
        this.props.segmentId &&
        this.props.thankYouAdminSegments[this.props.segmentId],
      this.charityId()
    );
    return (
      <section className={this.props.hideTitle ? s.pageBody : s.block}>
        {!this.props.hideTitle && (
          <h2 className={s.title}>Assigned Thank You Users</h2>
        )}
        {!this.props.addOnly && this.renderRows(rows, this.handleRemove)}
        {!this.props.hideAdd &&
          this.renderAdd(
            userOptions,
            "All assignable users are already assigned to this segment."
          )}
        {this.state.error && <p className={s.error}>{this.state.error}</p>}
        <SnackbarNotification
          open={this.state.snackbarOpen}
          message={this.state.snackbarMessage}
          severity={this.state.snackbarSeverity}
          onClose={() => this.setState({ snackbarOpen: false })}
        />
      </section>
    );
  }
}

const GLOBAL_SCOPE = "__global__";

class UserThankYouAssignmentBlockInner extends ThankYouAdminsPanel {
  addAssignments = () => {
    const parsed = readThresholds(this.state.addFields);
    const userId = this.props.userId;
    const scopes = this.state.addScopeIds;
    if (!parsed || !userId || scopes.length === 0) {
      this.setState({
        error:
          "Choose Global or at least one segment, and enter valid whole-dollar ranges.",
      });
      return;
    }
    const includeGlobal = scopes.indexOf(GLOBAL_SCOPE) >= 0;
    const segmentIds = scopes.filter(scope => scope !== GLOBAL_SCOPE);
    this.setState({ busy: true, error: "" }, () => {
      const writes: Array<Promise<unknown>> = [];
      if (includeGlobal) {
        writes.push(
          upsertThankYouAdminGlobal(
            this.adminUrl(),
            this.charityId(),
            userId,
            this.token(),
            parsed.threshold,
            parsed.firstMonthlyThreshold,
            parsed.maxThreshold,
            parsed.maxFirstMonthlyThreshold
          )
        );
      }
      if (segmentIds.length > 0) {
        writes.push(
          bulkUpsertThankYouAdminLists(
            this.adminUrl(),
            this.charityId(),
            this.token(),
            userId,
            segmentIds,
            parsed.threshold,
            parsed.firstMonthlyThreshold,
            parsed.maxThreshold,
            parsed.maxFirstMonthlyThreshold
          )
        );
      }
      Promise.all(writes)
        .then(() => {
          this.showToast("Thank-you assignments saved");
          this.setState({
            addScopeIds: [],
            addFields: { ...BLANK_THRESHOLDS },
          });
          if (this.props.onAddSuccess) this.props.onAddSuccess();
        })
        .catch(this.showError);
    });
  };

  saveAssignment = () => {
    const parsed = readThresholds(this.state.editFields);
    const userId = this.props.userId;
    const scope = this.state.editScopeId;
    if (!parsed || !userId || !scope) {
      this.setState({ error: "Enter valid whole-dollar ranges." });
      return;
    }
    if (scope === GLOBAL_SCOPE) {
      this.upsertGlobal(userId, parsed);
      return;
    }
    this.upsertList(scope, userId, parsed);
  };

  onScopeChange = (
    selection: Array<{ label: string; value: string }> | null
  ) => {
    const values = (selection || []).map(option => option.value);
    this.setState({ addScopeIds: values });
  };

  removeList = (segmentId: string) => {
    this.runWrite(
      () =>
        removeThankYouAdminList(
          this.adminUrl(),
          this.charityId(),
          segmentId,
          this.props.userId,
          this.token()
        ),
      "Thank you segment assignment removed"
    );
  };

  render() {
    const userId = this.props.userId;
    if (!userId) {
      return null;
    }
    const globalRow =
      this.props.thankYouAdminEmails && this.props.thankYouAdminEmails[userId];
    const listRows = listIdsForUser(this.props.thankYouAdminSegments, userId);
    const assignedIds = new Set(listRows.map(row => row.segmentId));
    const scopeOptions = [
      ...(globalRow ? [] : [{ label: "Global", value: GLOBAL_SCOPE }]),
      ...Object.keys(this.props.segments || {})
        .filter(id => isOrdinaryDonorList(this.props.segments[id]))
        .filter(id => !assignedIds.has(id))
        .map(id => ({
          label: this.props.segments[id].name,
          value: id,
        })),
    ];
    const selectedScopes = scopeOptions.filter(
      option => this.state.addScopeIds.indexOf(option.value) >= 0
    );
    const canEdit = this.canEdit();
    const rows: Array<{
      scopeId: string;
      name: string;
      threshold: number | null | undefined;
      maxThreshold: number | null | undefined;
      firstMonthlyThreshold: number | null | undefined;
      maxFirstMonthlyThreshold: number | null | undefined;
    }> = [
      ...(globalRow
        ? [
            {
              scopeId: GLOBAL_SCOPE,
              name: "Global",
              threshold: globalRow.threshold,
              maxThreshold: globalRow.maxThreshold,
              firstMonthlyThreshold: globalRow.firstMonthlyThreshold,
              maxFirstMonthlyThreshold: globalRow.maxFirstMonthlyThreshold,
            },
          ]
        : []),
      ...listRows.map(({ segmentId, row }) => ({
        scopeId: segmentId,
        name:
          this.props.segments && this.props.segments[segmentId]
            ? this.props.segments[segmentId].name
            : segmentId,
        threshold: row.threshold,
        maxThreshold: row.maxThreshold,
        firstMonthlyThreshold: row.firstMonthlyThreshold,
        maxFirstMonthlyThreshold: row.maxFirstMonthlyThreshold,
      })),
    ];
    return (
      <section className={this.props.hideTitle ? s.pageBody : s.block}>
        {!this.props.hideTitle && <h2 className={s.title}>Thank You Users</h2>}
        {!this.props.addOnly &&
          (rows.length === 0 ? (
            <p className={s.empty}>No thank-you assignments.</p>
          ) : (
            <table className={s.table}>
              <thead>
                <tr>
                  <th>Assignment</th>
                  <th className={s.amount}>Gift min ($)</th>
                  <th className={s.amount}>Gift max ($)</th>
                  <th className={s.amount}>First monthly min ($)</th>
                  <th className={s.amount}>First monthly max ($)</th>
                  {canEdit && <th className={s.actions}> </th>}
                </tr>
              </thead>
              <tbody>
                {rows.map(row => {
                  const editing = this.state.editScopeId === row.scopeId;
                  return (
                    <tr key={row.scopeId}>
                      <td>{row.name}</td>
                      <td className={s.amount}>
                        {editing ? (
                          <CellNumberInput
                            name={`user-edit-threshold-${row.scopeId}`}
                            id={`user-edit-threshold-${row.scopeId}`}
                            value={this.state.editFields.threshold}
                            onChange={threshold =>
                              this.setState({
                                editFields: {
                                  ...this.state.editFields,
                                  threshold,
                                },
                              })
                            }
                          />
                        ) : (
                          formatDollarAmount(row.threshold)
                        )}
                      </td>
                      <td className={s.amount}>
                        {editing ? (
                          <CellNumberInput
                            name={`user-edit-max-threshold-${row.scopeId}`}
                            id={`user-edit-max-threshold-${row.scopeId}`}
                            value={this.state.editFields.maxThreshold}
                            onChange={maxThreshold =>
                              this.setState({
                                editFields: {
                                  ...this.state.editFields,
                                  maxThreshold,
                                },
                              })
                            }
                          />
                        ) : (
                          formatDollarAmount(row.maxThreshold)
                        )}
                      </td>
                      <td className={s.amount}>
                        {editing ? (
                          <CellNumberInput
                            name={`user-edit-monthly-${row.scopeId}`}
                            id={`user-edit-monthly-${row.scopeId}`}
                            value={this.state.editFields.firstMonthlyThreshold}
                            onChange={firstMonthlyThreshold =>
                              this.setState({
                                editFields: {
                                  ...this.state.editFields,
                                  firstMonthlyThreshold,
                                },
                              })
                            }
                          />
                        ) : (
                          formatDollarAmount(row.firstMonthlyThreshold)
                        )}
                      </td>
                      <td className={s.amount}>
                        {editing ? (
                          <CellNumberInput
                            name={`user-edit-max-monthly-${row.scopeId}`}
                            id={`user-edit-max-monthly-${row.scopeId}`}
                            value={
                              this.state.editFields.maxFirstMonthlyThreshold
                            }
                            onChange={maxFirstMonthlyThreshold =>
                              this.setState({
                                editFields: {
                                  ...this.state.editFields,
                                  maxFirstMonthlyThreshold,
                                },
                              })
                            }
                          />
                        ) : (
                          formatDollarAmount(row.maxFirstMonthlyThreshold)
                        )}
                      </td>
                      {canEdit && (
                        <td className={s.actions}>
                          {editing ? (
                            <div className={s.rowActions}>
                              <Button
                                className={s.button}
                                disabled={this.state.busy}
                                onClick={this.saveAssignment}
                              >
                                Save
                              </Button>
                              <Button
                                className={s.button}
                                onClick={() =>
                                  this.setState({ editScopeId: null })
                                }
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className={s.rowActions}>
                              {this.renderIconButton("Edit", "fa-edit", () =>
                                this.setState({
                                  editScopeId: row.scopeId,
                                  editFields: {
                                    threshold: String(
                                      row.threshold == null ? "" : row.threshold
                                    ),
                                    maxThreshold: String(
                                      row.maxThreshold == null
                                        ? ""
                                        : row.maxThreshold
                                    ),
                                    firstMonthlyThreshold: String(
                                      row.firstMonthlyThreshold == null
                                        ? ""
                                        : row.firstMonthlyThreshold
                                    ),
                                    maxFirstMonthlyThreshold: String(
                                      row.maxFirstMonthlyThreshold == null
                                        ? ""
                                        : row.maxFirstMonthlyThreshold
                                    ),
                                  },
                                })
                              )}
                              {this.renderIconButton(
                                "Remove",
                                "fa-trash",
                                () =>
                                  row.scopeId === GLOBAL_SCOPE
                                    ? this.runWrite(
                                        () =>
                                          removeThankYouAdminGlobal(
                                            this.adminUrl(),
                                            this.charityId(),
                                            userId,
                                            this.token()
                                          ),
                                        "Removed from Global"
                                      )
                                    : this.removeList(row.scopeId),
                                this.state.busy
                              )}
                            </div>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ))}
        {canEdit && !this.props.hideAdd && (
          <div className={s.addRow}>
            <FormGroup className={s.staffSelect}>
              <label className={s.label} htmlFor="user-scope">
                Assignment
              </label>
              <BaseSelect
                id="user-scope"
                name="user-scope"
                dataElmId="user-scope"
                isMulti
                placeholder="Select Global or segments..."
                options={scopeOptions}
                value={selectedScopes}
                onChange={this.onScopeChange}
              />
            </FormGroup>
            <ThresholdFields
              idPrefix="user-add"
              fields={this.state.addFields}
              disabled={this.state.busy}
              onChange={addFields => this.setState({ addFields })}
            />
            <Button
              className={s.button}
              disabled={this.state.busy || this.state.addScopeIds.length === 0}
              onClick={this.addAssignments}
            >
              Add
            </Button>
          </div>
        )}
        {this.state.error && <p className={s.error}>{this.state.error}</p>}
        <SnackbarNotification
          open={this.state.snackbarOpen}
          message={this.state.snackbarMessage}
          severity={this.state.snackbarSeverity}
          onClose={() => this.setState({ snackbarOpen: false })}
        />
      </section>
    );
  }
}

class ThankYouAdminBulkActionInner extends ThankYouAdminsPanel {
  bulkAdd = () => {
    const parsed = readThresholds(this.state.addFields);
    const segmentIds = this.props.selectedSegmentIds || [];
    if (!parsed || !this.state.addUserId) {
      this.setState({
        error: "Choose a user and enter valid whole-dollar ranges.",
      });
      return;
    }
    if (!segmentIds.some(id => this.isOrdinarySelected(id))) {
      this.setState({ error: "Select at least one ordinary segment." });
      return;
    }
    this.closeBulk();
    this.setState({ busy: true, error: "" }, () => {
      bulkUpsertThankYouAdminLists(
        this.adminUrl(),
        this.charityId(),
        this.token(),
        this.state.addUserId,
        segmentIds,
        parsed.threshold,
        parsed.firstMonthlyThreshold,
        parsed.maxThreshold,
        parsed.maxFirstMonthlyThreshold
      )
        .then(result => {
          this.showToast(
            `Updated ${result.updatedCount} list${
              result.updatedCount === 1 ? "" : "s"
            }, skipped ${result.skippedCount}.`
          );
          this.setState({
            addUserId: "",
            addFields: { ...BLANK_THRESHOLDS },
          });
        })
        .catch(this.showError);
    });
  };

  closeBulk = () => {
    if (this.props.onClose) {
      this.props.onClose();
    }
    this.setState({ error: "" });
  };

  isOrdinarySelected = (segmentId: string) => {
    const segment = this.props.segments && this.props.segments[segmentId];
    return segment ? isOrdinaryDonorList(segment) : true;
  };

  render() {
    if (!this.canEdit()) {
      return null;
    }
    const segmentIds = this.props.selectedSegmentIds || [];
    const hasOrdinary = segmentIds.some(id => this.isOrdinarySelected(id));
    const userOptions = assignableStaffOptions(
      this.props.users,
      this.charityId()
    ).filter(option =>
      segmentIds.some(segmentId => {
        if (!this.isOrdinarySelected(segmentId)) {
          return false;
        }
        return !isStaffAlreadyAssigned(
          option,
          assignedStaffKeys(
            this.props.thankYouAdminSegments &&
              this.props.thankYouAdminSegments[segmentId]
          )
        );
      })
    );
    return (
      <Fragment>
        <ModalFrame
          title="Add thank you user"
          isOpen={!!this.props.isOpen}
          size="lg"
          toggle={this.closeBulk}
        >
          {userOptions.length === 0 ? (
            <p className={s.empty}>
              All assignable users are already assigned to the selected
              segments.
            </p>
          ) : (
            <Fragment>
              <FormGroup className={s.staffSelect}>
                <label className={s.label} htmlFor="bulk-user">
                  User
                </label>
                <BaseSelect
                  id="bulk-user"
                  name="bulk-user"
                  dataElmId="bulk-user"
                  placeholder="Select user..."
                  options={userOptions.map(option => ({
                    label: `${option.label} (${option.email})`,
                    value: option.id,
                  }))}
                  value={
                    userOptions
                      .map(option => ({
                        label: `${option.label} (${option.email})`,
                        value: option.id,
                      }))
                      .find(option => option.value === this.state.addUserId) ||
                    null
                  }
                  onChange={selection =>
                    this.setState({
                      addUserId: selection ? selection.value : "",
                    })
                  }
                />
              </FormGroup>
              <ThresholdFields
                idPrefix="bulk-add"
                fields={this.state.addFields}
                disabled={this.state.busy}
                onChange={addFields => this.setState({ addFields })}
              />
            </Fragment>
          )}
          {this.state.error && <p className={s.error}>{this.state.error}</p>}
          <div className={s.bulkModalActions}>
            <Button className={s.button} onClick={this.closeBulk}>
              Cancel
            </Button>
            <Button
              className={s.primaryButton}
              disabled={
                this.state.busy ||
                !this.state.addUserId ||
                !hasOrdinary ||
                userOptions.length === 0
              }
              onClick={this.bulkAdd}
            >
              Add
            </Button>
          </div>
        </ModalFrame>
        <SnackbarNotification
          open={this.state.snackbarOpen}
          message={this.state.snackbarMessage}
          severity={this.state.snackbarSeverity}
          onClose={() => this.setState({ snackbarOpen: false })}
        />
      </Fragment>
    );
  }
}

function mapSharedState(state): SharedDataProps {
  const charityId = state.user.currentCharity.id;
  return {
    thankYouAdminEmails: get(
      state.firebase,
      `data.${charityId}.internal.emails.thankYouAdminEmails`
    ),
    thankYouAdminSegments: get(
      state.firebase,
      `data.${charityId}.thankYouAdminSegments`
    ),
    users: get(state.firebase, `data.${charityId}.users`),
    segments: get(state.firebase, `data.${charityId}.segments`),
  };
}

function thankYouFirebasePaths(_, store) {
  const charityId = store.getState().user.currentCharity.id;
  if (!charityId) {
    return [];
  }
  return [
    `${charityId}/internal/emails/thankYouAdminEmails`,
    `${charityId}/thankYouAdminSegments`,
    `${charityId}/users`,
    `${charityId}/segments`,
  ];
}

const styledGlobal = withStyles(s)(GlobalThankYouAdminsBlockInner);
const styledSegment = withStyles(s)(SegmentThankYouAdminsBlockInner);
const styledUser = withStyles(s)(UserThankYouAssignmentBlockInner);
const styledBulk = withStyles(s)(ThankYouAdminBulkActionInner);

export const GlobalThankYouAdminsBlock = serviceCaller(
  connect(mapSharedState)(firebaseConnect(thankYouFirebasePaths)(styledGlobal))
);

export const SegmentThankYouAdminsBlock = serviceCaller(
  connect(mapSharedState)(firebaseConnect(thankYouFirebasePaths)(styledSegment))
);

export const UserThankYouAssignmentBlock = serviceCaller(
  connect(mapSharedState)(firebaseConnect(thankYouFirebasePaths)(styledUser))
);

export const ThankYouAdminBulkAction = serviceCaller(
  connect(mapSharedState)(firebaseConnect(thankYouFirebasePaths)(styledBulk))
);
